/**
 * A tiny presentation gate used to coordinate client-only pre-spin animations
 * with server-authoritative replay.
 *
 * Pattern:
 *  - hold("spin") on click
 *  - start preSpin animation + requestPlay in parallel
 *  - release("spin") when preSpin finishes
 *  - replay ports await wait("spin") before touching grid state (prepare/reveal)
 */
export class PresentationBarrier {
    private readonly holds: Map<string, { count: number; promise: Promise<void>; resolve: () => void }> = new Map();
  
    public hold(key: string): void {
      const existing = this.holds.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }
  
      let resolve!: () => void;
      const promise = new Promise<void>((r) => {
        resolve = r;
      });
  
      this.holds.set(key, { count: 1, promise, resolve });
    }
  
    public release(key: string): void {
      const existing = this.holds.get(key);
      if (!existing) return;
  
      existing.count -= 1;
      if (existing.count > 0) return;
  
      this.holds.delete(key);
      existing.resolve();
    }
  
    public isHeld(key: string): boolean {
      return this.holds.has(key);
    }
  
    public wait(key: string): Promise<void> {
      const existing = this.holds.get(key);
      return existing ? existing.promise : Promise.resolve();
    }
  }